import React from 'react';

const PriorityOpportunities = ({ displayData, sampleDataFallback }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
          🎯 Priority Opportunities Analysis
        </h2>
        {displayData.isRealAnalysis && (
          <span className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 px-3 py-1 rounded-full text-sm font-medium">
            ✅ Real Analysis
          </span>
        )}
      </div>

      {displayData.isRealAnalysis && displayData.realCategories ? (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 border-2 border-emerald-200 dark:border-emerald-700 rounded-lg p-6">
            <h3 className="text-xl font-bold text-emerald-800 dark:text-emerald-300 mb-4">
              ✅ Professional Analysis Complete - Category Breakdown
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Our equipment-based analysis engine has identified{' '}
              {displayData.measuresCount} opportunities across{' '}
              {displayData.realCategories.length} categories with total annual
              savings of ${displayData.annualSavings.toLocaleString()}.
            </p>

            {/* Category Breakdown Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayData.realCategories.map((category, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-emerald-200 dark:border-emerald-700 hover:shadow-lg transition-shadow"
                >
                  <div className="text-center">
                    <h4 className="text-lg font-bold text-slate-800 dark:text-white mb-2">
                      {category.name}
                    </h4>
                    <div className="space-y-2">
                      <div>
                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                          {category.count}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Opportunities Found
                        </p>
                      </div>
                      <div>
                        <p className="text-xl font-semibold text-slate-700 dark:text-slate-300">
                          ${category.savings.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Annual Savings
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-medium text-blue-600 dark:text-blue-400">
                          {category.energySaved.toLocaleString()} kWh
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Energy Savings
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 text-center">
                    <div className="bg-emerald-100 dark:bg-emerald-900/40 p-3 rounded-lg">
                      <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">
                        ✅ Specific measures & implementation details included
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Methodology Explanation */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
            <h3 className="text-lg font-bold text-blue-800 dark:text-blue-300 mb-3">
              🔬 How Our Analysis Works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl mb-2">🏭</div>
                <h4 className="font-semibold text-blue-700 dark:text-blue-400">
                  Equipment Profiling
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Map facility equipment to specific efficiency opportunities
                </p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">📊</div>
                <h4 className="font-semibold text-blue-700 dark:text-blue-400">
                  DOE+RASA Models
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Professional calculations using industry databases
                </p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">💰</div>
                <h4 className="font-semibold text-blue-700 dark:text-blue-400">
                  Financial Modeling
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Real ROI calculations with facility scaling
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
            <p className="text-amber-800 dark:text-amber-300">
              <strong>Sample Mode:</strong> This shows representative
              opportunities for demonstration. Real analysis uses
              equipment-specific profiling for personalized recommendations.
            </p>
          </div>

          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Sample opportunities shown for demonstration:
            </p>
            {sampleDataFallback.priorities.slice(0, 3).map((opp, index) => (
              <div
                key={opp.id}
                className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-slate-600 dark:bg-slate-700 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-800 dark:text-white">
                        {opp.measure}
                      </h3>
                      <span className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-3 py-1 rounded-full text-sm">
                        {opp.category}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-600 dark:text-slate-300">
                      ${opp.savings.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {opp.payback} years payback
                    </p>
                  </div>
                </div>

                <p className="text-gray-700 dark:text-gray-300 mt-4">
                  {opp.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Professional Analysis Complete */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 border-2 border-emerald-200 dark:border-emerald-700 rounded-lg p-6 text-center">
        <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
          ✓ Complete Professional Analysis & Implementation Plans Available
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {displayData.isRealAnalysis
            ? 'You now have access to our complete professional methodology with detailed implementation roadmaps, technical specifications, and comprehensive guidance.'
            : 'This demonstrates our complete professional analysis capabilities with detailed implementation roadmaps and comprehensive technical guidance.'}
        </p>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg mb-4 border border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-2">
            ✅ What&apos;s Included in This Professional Analysis:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-left">
            <ul className="space-y-1 text-gray-700 dark:text-gray-300">
              <li>• Specific measure titles & technical details</li>
              <li>• Implementation cost breakdowns</li>
              <li>• Partner company recommendations</li>
              <li>• Funding opportunity matching</li>
            </ul>
            <ul className="space-y-1 text-gray-700 dark:text-gray-300">
              <li>• Phased implementation roadmaps</li>
              <li>• Risk assessment & success rates</li>
              <li>• Executive summary (PDF export)</li>
              <li>• Professional consultation included</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriorityOpportunities;


